import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

/**
 * /enviar-paquete/success — mirrors CheckoutSuccess but for shipments.
 * On paid → navigate to /envio/{shipment_id}.
 */
export default function EnviarPaqueteSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const attempts = useRef(0);
  const stopped = useRef(false);
  const [state, setState] = useState({ kind: "polling", message: "Confirmando tu pago…" });

  const MAX_ATTEMPTS = 15;
  const INTERVAL_MS = 2000;

  const poll = useCallback(async () => {
    if (stopped.current) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.status === "paid") {
        stopped.current = true;
        toast.success("Pago confirmado. ¡Tu envío está en marcha!");
        if (data.related_id) navigate(`/envio/${data.related_id}`, { replace: true });
        return;
      }
      if (data.status === "expired") {
        stopped.current = true;
        setState({
          kind: "expired",
          message: "La sesión de pago ha caducado. Puedes volver a crear el envío.",
        });
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        stopped.current = true;
        setState({
          kind: "timeout",
          message:
            "Aún no hemos confirmado tu pago. Recibirás un email cuando se procese.",
        });
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    } catch (_err) {
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        stopped.current = true;
        setState({
          kind: "timeout",
          message:
            "No hemos podido comprobar el estado del pago. Recibirás un email cuando se procese.",
        });
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "missing", message: "Falta el identificador de la sesión de pago." });
      return () => {};
    }
    stopped.current = false;
    attempts.current = 0;
    poll();
    return () => {
      stopped.current = true;
    };
  }, [sessionId, poll]);

  return (
    <div
      data-testid="enviar-paquete-success-page"
      className="mx-auto max-w-lg px-6 py-16 sm:py-24 text-center"
    >
      {state.kind === "polling" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-[#F1FAF3] grid place-items-center">
            <Loader2 className="h-8 w-8 text-[#00953B] animate-spin" data-testid="enviar-success-spinner" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="enviar-success-title">
            Confirmando tu pago…
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">
            {state.message} No cierres esta ventana.
          </p>
        </>
      )}
      {state.kind === "expired" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-red-50 grid place-items-center">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="enviar-success-expired">
            Sesión de pago caducada
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white h-11 px-6"
          >
            <Link to="/enviar-paquete/nuevo">Volver a intentar</Link>
          </Button>
        </>
      )}
      {state.kind === "timeout" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-amber-50 grid place-items-center">
            <CheckCircle2 className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="enviar-success-timeout">
            Pago en proceso
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            variant="outline"
            className="mt-6 rounded-full h-11 px-6"
          >
            <Link to="/mis-envios">Ver mis envíos</Link>
          </Button>
        </>
      )}
      {state.kind === "missing" && (
        <>
          <h1 className="text-2xl font-black tracking-tight">Enlace incompleto</h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white h-11 px-6"
          >
            <Link to="/enviar-paquete/nuevo">Volver a intentar</Link>
          </Button>
        </>
      )}
    </div>
  );
}
